export type Workspace = "marketplace" | "registration" | "matching" | "patient" | "specialist" | "admin";

export type RegistrationRole = "admin" | "specialist" | "client";

export type AdminBootstrapProfile = {
  lastName: string;
  firstName: string;
  middleName: string;
  dateOfBirth: string;
  fullName: string;
  email: string;
  phone: string;
  password: string;
  roleTitle: string;
  organization: string;
  accessLevel: string;
  twoFactorChannel: string;
  invitePolicy: string;
  launchNotes: string;
  acceptedPlatformRules: boolean;
  acceptedDataRules: boolean;
};

export type SpecialistRegistration = {
  lastName: string;
  firstName: string;
  middleName: string;
  dateOfBirth: string;
  legalName: string;
  publicName: string;
  email: string;
  phone: string;
  city: string;
  timezone: string;
  legalStatus: string;
  professionalRole: string;
  education: string;
  degree: string;
  certificates: string;
  diplomaFiles: string[];
  certificateFiles: string[];
  licenseFiles: string[];
  experienceYears: string;
  practiceHours: string;
  supervision: string;
  personalTherapy: string;
  approaches: string[];
  topics: string;
  worksWith: string;
  doesNotWorkWith: string;
  formats: string[];
  languages: string;
  introSession: number;
  regularSession: number;
  packageFour: number;
  packageEight: number;
  socialSlot: number;
  portfolioLinks: string;
  socialLinks: string;
  introVideoUrl: string;
  bio: string;
  moderationComment: string;
  acceptedVerification: boolean;
  acceptedPlatformRules: boolean;
};

export type ClientRegistration = {
  lastName: string;
  firstName: string;
  middleName: string;
  dateOfBirth: string;
  fullName: string;
  nickname: string;
  age: string;
  email: string;
  phone: string;
  city: string;
  timezone: string;
  preferredContact: string;
  emergencyContact: string;
  mainComplaint: string;
  complaintDuration: string;
  therapyGoals: string;
  symptoms: string[];
  priorTherapy: string;
  medicationOrDoctor: string;
  riskNotes: string;
  formatPreference: string;
  budget: string;
  therapistPreference: string;
  scheduleWindows: string;
  privacyMode: boolean;
  acceptedProcessing: boolean;
  acceptedPlatformRules: boolean;
};

export type RegistrationState = {
  activeRole: RegistrationRole | "";
  adminStatus: "Черновик" | "Готов к запуску";
  specialistStatus: "Черновик" | "Отправлено на модерацию";
  clientStatus: "Черновик" | "Анкета сохранена";
  admin: AdminBootstrapProfile;
  specialist: SpecialistRegistration;
  client: ClientRegistration;
};

export type PatientProfile = {
  displayName: string;
  age: string;
  city: string;
  timezone: string;
  preferredContact: string;
  privacyMode: boolean;
  currentRequest: string;
  therapyGoal: string;
  preferredFormat: string;
  budget: string;
  specialistPreference: string;
  sessionCadence: string;
  moodScore: number;
  emotionalState: string;
  replacementReason: string;
};

export type PricingModel = {
  introSession: number;
  regularSession: number;
  packageFour: number;
  packageEight: number;
  socialSlot: number;
  platformCommission: number;
  cancellationWindowHours: number;
};

export type SpecialistProfile = {
  publicName: string;
  professionalTitle: string;
  level: string;
  city: string;
  experienceYears: string;
  education: string;
  certificates: string;
  approaches: string[];
  worksWith: string;
  doesNotWorkWith: string;
  formats: string[];
  bio: string;
  pricing: PricingModel;
  moderationStatus: "Черновик" | "На проверке" | "Одобрен";
  profileCompleteness: number;
};

export type AdminApplication = {
  id: string;
  name: string;
  role: string;
  status: "Новая" | "Документы" | "Интервью" | "Одобрить";
  submitted: string;
};

export type AdminBooking = {
  id: string;
  time: string;
  pair: string;
  amount: number;
  status: "Ожидает" | "Подтверждено" | "Отмена";
};

export type AdminCase = {
  id: string;
  title: string;
  participants: string;
  status: "Новый" | "В работе" | "Решено";
  priority: "Низкий" | "Средний" | "Высокий";
};

export type AdminState = {
  applications: AdminApplication[];
  bookings: AdminBooking[];
  cases: AdminCase[];
  fiscalizationMode: "Не подключено" | "Песочница" | "Готово";
};

export type ClientMarketplaceRequest = {
  id: string;
  createdAt: string;
  selectedService: string;
  selectedSpecialist: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  telegram: string;
  vkId: string;
  confirmationChannel: string;
  contactConfirmed: boolean;
  mainComplaint: string;
  therapyGoals: string;
  symptoms: string[];
  formatPreference: string;
  budget: string;
  scheduleWindows: string;
  status: "Новая заявка" | "Контакт подтверждён" | "Передана в подбор";
};

export type SpecialistMarketplaceApplication = {
  id: string;
  createdAt: string;
  publicName: string;
  legalName: string;
  email: string;
  phone: string;
  telegram: string;
  vkId: string;
  confirmationChannel: string;
  contactConfirmed: boolean;
  professionalRole: string;
  city: string;
  education: string;
  experienceYears: string;
  serviceTitle: string;
  serviceDescription: string;
  formats: string[];
  approaches: string[];
  regularSession: number;
  introSession: number;
  portfolioLinks: string;
  status: "Новая заявка" | "Контакт подтверждён" | "На модерации";
};

export type MarketplaceState = {
  clientRequests: ClientMarketplaceRequest[];
  specialistApplications: SpecialistMarketplaceApplication[];
};

export const workspaceTabs: Array<{ id: Workspace; label: string }> = [
  { id: "marketplace", label: "Маркетплейс" },
  { id: "registration", label: "Регистрация" },
  { id: "matching", label: "Подбор" },
  { id: "patient", label: "Пациент" },
  { id: "specialist", label: "Специалист" },
  { id: "admin", label: "Админ" },
];

export const defaultRegistrationState: RegistrationState = {
  activeRole: "",
  adminStatus: "Черновик",
  specialistStatus: "Черновик",
  clientStatus: "Черновик",
  admin: {
    lastName: "",
    firstName: "",
    middleName: "",
    dateOfBirth: "",
    fullName: "",
    email: "",
    phone: "",
    password: "",
    roleTitle: "",
    organization: "",
    accessLevel: "",
    twoFactorChannel: "",
    invitePolicy: "",
    launchNotes: "",
    acceptedPlatformRules: false,
    acceptedDataRules: false,
  },
  specialist: {
    lastName: "",
    firstName: "",
    middleName: "",
    dateOfBirth: "",
    legalName: "",
    publicName: "",
    email: "",
    phone: "",
    city: "",
    timezone: "",
    legalStatus: "",
    professionalRole: "",
    education: "",
    degree: "",
    certificates: "",
    diplomaFiles: [],
    certificateFiles: [],
    licenseFiles: [],
    experienceYears: "",
    practiceHours: "",
    supervision: "",
    personalTherapy: "",
    approaches: [],
    topics: "",
    worksWith: "",
    doesNotWorkWith: "",
    formats: [],
    languages: "",
    introSession: 0,
    regularSession: 0,
    packageFour: 0,
    packageEight: 0,
    socialSlot: 0,
    portfolioLinks: "",
    socialLinks: "",
    introVideoUrl: "",
    bio: "",
    moderationComment: "",
    acceptedVerification: false,
    acceptedPlatformRules: false,
  },
  client: {
    lastName: "",
    firstName: "",
    middleName: "",
    dateOfBirth: "",
    fullName: "",
    nickname: "",
    age: "",
    email: "",
    phone: "",
    city: "",
    timezone: "",
    preferredContact: "",
    emergencyContact: "",
    mainComplaint: "",
    complaintDuration: "",
    therapyGoals: "",
    symptoms: [],
    priorTherapy: "",
    medicationOrDoctor: "",
    riskNotes: "",
    formatPreference: "",
    budget: "",
    therapistPreference: "",
    scheduleWindows: "",
    privacyMode: false,
    acceptedProcessing: false,
    acceptedPlatformRules: false,
  },
};

export const defaultPatientProfile: PatientProfile = {
  displayName: "Мария Г.",
  age: "32",
  city: "Москва",
  timezone: "GMT+3",
  preferredContact: "Telegram",
  privacyMode: true,
  currentRequest: "Тревожность и выгорание",
  therapyGoal: "Понять причины напряжения, восстановить ресурс и подобрать регулярный формат работы.",
  preferredFormat: "Видео",
  budget: "5-8 тыс. ₽",
  specialistPreference: "Женщина, senior, доказательные методы",
  sessionCadence: "1 раз в неделю",
  moodScore: 3,
  emotionalState: "Тревога",
  replacementReason: "",
};

export const defaultSpecialistProfile: SpecialistProfile = {
  publicName: "Анна Смирнова",
  professionalTitle: "Психолог, КПТ / ACT / EMDR",
  level: "senior",
  city: "Москва",
  experienceYears: "7",
  education: "МГППУ, клиническая психология; программа КПТ, 2021",
  certificates: "ACT в практике, EMDR Europe, регулярная супервизия",
  approaches: ["КПТ", "ACT", "EMDR"],
  worksWith: "Тревога, выгорание, стресс, самооценка, мягкие депрессивные состояния.",
  doesNotWorkWith:
    "Острые кризисные состояния, зависимости, расстройства пищевого поведения без профильной команды.",
  formats: ["Видео", "Чат"],
  bio: "Работаю структурно и бережно: сначала проясняем запрос, затем выбираем темп и понятные шаги между сессиями.",
  pricing: {
    introSession: 4500,
    regularSession: 6000,
    packageFour: 22000,
    packageEight: 42000,
    socialSlot: 3000,
    platformCommission: 35,
    cancellationWindowHours: 24,
  },
  moderationStatus: "На проверке",
  profileCompleteness: 82,
};

export const defaultAdminState: AdminState = {
  applications: [
    {
      id: "#512",
      name: "Наталья Кузнецова",
      role: "Психолог",
      status: "Документы",
      submitted: "18.06",
    },
    {
      id: "#511",
      name: "Дмитрий Орлов",
      role: "Психотерапевт",
      status: "Интервью",
      submitted: "17.06",
    },
    {
      id: "#510",
      name: "Ольга Морозова",
      role: "Психолог",
      status: "Новая",
      submitted: "16.06",
    },
  ],
  bookings: [
    {
      id: "#1247",
      time: "19:00",
      pair: "Мария Г. -> Мария Волкова",
      amount: 6500,
      status: "Подтверждено",
    },
    {
      id: "#1248",
      time: "20:00",
      pair: "Алексей П. -> Игорь Лебедев",
      amount: 5500,
      status: "Ожидает",
    },
    {
      id: "#1249",
      time: "21:00",
      pair: "Елена С. -> Анна Смирнова",
      amount: 6000,
      status: "Подтверждено",
    },
  ],
  cases: [
    {
      id: "#2387",
      title: "Запрос на замену",
      participants: "Мария Г. / Мария Волкова",
      status: "Новый",
      priority: "Средний",
    },
    {
      id: "#2386",
      title: "Жалоба на коммуникацию",
      participants: "Алексей П. / Игорь Лебедев",
      status: "В работе",
      priority: "Высокий",
    },
    {
      id: "#2385",
      title: "Возврат после отмены",
      participants: "Елена С. / Анна Смирнова",
      status: "Решено",
      priority: "Низкий",
    },
  ],
  fiscalizationMode: "Не подключено",
};

export const defaultMarketplaceState: MarketplaceState = {
  clientRequests: [
    {
      id: "CL-1001",
      createdAt: "20.06.2026",
      selectedService: "Первичная консультация",
      selectedSpecialist: "Мария Волкова",
      firstName: "Мария",
      lastName: "Г.",
      dateOfBirth: "1994-02-18",
      email: "demo-client@example.com",
      phone: "+7 900 000-00-01",
      telegram: "@demo_client",
      vkId: "",
      confirmationChannel: "Telegram",
      contactConfirmed: true,
      mainComplaint: "Тревога и выгорание, нужен бережный старт.",
      therapyGoals: "Понять причины напряжения и выбрать регулярный формат работы.",
      symptoms: ["Тревога", "Выгорание"],
      formatPreference: "Видео",
      budget: "5-8 тыс. ₽",
      scheduleWindows: "Будни после 19:00",
      status: "Передана в подбор",
    },
  ],
  specialistApplications: [
    {
      id: "SP-2001",
      createdAt: "20.06.2026",
      publicName: "Анна Смирнова",
      legalName: "Смирнова Анна Сергеевна",
      email: "demo-specialist@example.com",
      phone: "+7 900 000-00-02",
      telegram: "@demo_specialist",
      vkId: "",
      confirmationChannel: "E-mail",
      contactConfirmed: true,
      professionalRole: "Психолог, КПТ / ACT",
      city: "Москва",
      education: "МГППУ, клиническая психология",
      experienceYears: "7",
      serviceTitle: "Индивидуальные консультации",
      serviceDescription: "Работаю с тревогой, выгоранием и стрессом в структурном формате.",
      formats: ["Видео", "Чат"],
      approaches: ["КПТ", "ACT"],
      regularSession: 6000,
      introSession: 4500,
      portfolioLinks: "https://example.com/profile",
      status: "На модерации",
    },
  ],
};

export const demoSchedule = [
  ["10:00", "", "10:00", "", "", "", ""],
  ["12:00", "12:00", "", "12:00", "", "", ""],
  ["", "", "", "", "14:00", "14:00", "14:00"],
  ["18:00", "18:00", "18:00", "18:00", "18:00", "18:00", "18:00"],
  ["20:00", "", "", "", "20:00", "", ""],
];

export const scheduleDays = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
